import React, { useState, useEffect, useCallback } from 'react'
import { Modal, Tab, Nav, Dropdown } from 'react-bootstrap'
import CardForm from './CardForm.component'
import BankForm from './BankForm.component'
import OtherForm from './OtherForm.component'
import './AddPaymentModal.css'
import { SavedCard } from '@dataTypes/payment.types'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createSuccessToast,
  createFailedToast,
} from '@utils/notificationToast.util'

type AddPaymentMode = 'add' | 'edit'

type AddPaymentModalProps = {
  show: boolean
  onHide: () => void
  mode?: AddPaymentMode
  initialCard?: SavedCard | null
  onSubmit?: (card: SavedCard) => void
  patientId?: string
  clinicId?: string
  onCardAdded?: () => void
}

type PaymentMethod = 'PayPal' | 'Venmo' | 'CashApp' | 'Other'

const AddPaymentModal: React.FC<AddPaymentModalProps> = ({
  show,
  onHide,
  mode = 'add',
  initialCard,
  patientId,
  clinicId,
  onCardAdded,
}) => {
  const { showToast } = useNotificationToast()
  const [activeTab, setActiveTab] = useState('card')
  const [selectedOtherMethod, setSelectedOtherMethod] =
    useState<PaymentMethod>('Other')
  const [showOtherDropdown, setShowOtherDropdown] = useState(false)

  const handleOtherMethodSelect = (method: PaymentMethod): void => {
    setSelectedOtherMethod(method)
    setActiveTab('other')
    setShowOtherDropdown(false)
  }

  const handleTabSelect = (tab: string | null): void => {
    if (tab === 'other') {
      setShowOtherDropdown(!showOtherDropdown)
    } else {
      setActiveTab(tab || 'card')
      if (selectedOtherMethod !== 'Other') {
        setSelectedOtherMethod('Other')
      }
    }
  }

  useEffect((): void => {
    if (show && mode === 'edit' && initialCard) {
      setActiveTab('card')
      setSelectedOtherMethod('Other')
    }
    if (!show) {
      setActiveTab('card')
      setSelectedOtherMethod('Other')
    }
  }, [show, mode, initialCard])

  const handleCardSaveSuccess = useCallback((): void => {
    onCardAdded?.()
    showToast(
      createSuccessToast(
        'Payment method saved',
        'Your card has been saved for future use.',
      ),
    )
    onHide()
  }, [showToast, onHide, onCardAdded])

  const handleCardSaveError = useCallback(
    (message: string): void => {
      showToast(createFailedToast('Could not save payment method', message))
    },
    [showToast],
  )

  const tabItems =
    mode === 'edit'
      ? [{ eventKey: 'card', icon: 'ti ti-credit-card', label: 'Card' }]
      : [
          { eventKey: 'card', icon: 'ti ti-credit-card', label: 'Card' },
          { eventKey: 'bank', icon: 'ti ti-building-bank', label: 'Bank' },
          {
            eventKey: 'other',
            icon: 'ti ti-dots',
            label:
              selectedOtherMethod === 'Other' ? 'Other' : selectedOtherMethod,
            dropdown: true,
          },
        ]

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="add-payment-modal"
    >
      <Modal.Header closeButton className="bg-custom3">
        <Modal.Title>
          {mode === 'edit' ? 'Edit payment method' : 'Add payment method'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="add-payment-modal-body">
        <Tab.Container activeKey={activeTab} onSelect={handleTabSelect}>
          <Nav variant="tabs" className="mb-3 add-payment-tabs">
            {tabItems.map((item) => (
              <Nav.Item key={item.eventKey}>
                <Nav.Link
                  eventKey={item.eventKey}
                  className="position-relative"
                >
                  <div className="apm-tab-icon">
                    <i className={item.icon}></i>
                  </div>
                  {item.dropdown ? (
                    <>
                      <div className="apm-tab-label">
                        {selectedOtherMethod === 'Other'
                          ? 'Other'
                          : selectedOtherMethod}
                      </div>
                      {showOtherDropdown && (
                        <Dropdown.Menu
                          show
                          className="dropdown-menu-custom"
                          align="end"
                        >
                          <Dropdown.Item
                            onClick={() => handleOtherMethodSelect('PayPal')}
                          >
                            PayPal
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => handleOtherMethodSelect('CashApp')}
                          >
                            CashApp
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => handleOtherMethodSelect('Venmo')}
                          >
                            Venmo
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      )}
                    </>
                  ) : (
                    <div className="apm-tab-label">{item.label}</div>
                  )}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>

          <Tab.Content>
            <Tab.Pane eventKey="card">
              {patientId && clinicId ? (
                <CardForm
                  patientId={patientId}
                  clinicId={clinicId}
                  onSaveSuccess={handleCardSaveSuccess}
                  onSaveError={handleCardSaveError}
                  onClose={onHide}
                />
              ) : (
                <p className="text-muted small mb-0">
                  Select a patient and clinic to add a card.
                </p>
              )}
            </Tab.Pane>
            <Tab.Pane eventKey="bank">
              <BankForm />
            </Tab.Pane>
            <Tab.Pane eventKey="other">
              <OtherForm selectedMethod={selectedOtherMethod} />
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Modal.Body>
    </Modal>
  )
}

export default AddPaymentModal
